import { useEffect } from "react";

const SITE_URL = "https://tx1rs.com";

export interface SeoOptions {
  title: string;
  description: string;
  /** Route path (e.g. "/contact") used to build the canonical URL. Omit for pages that shouldn't declare a canonical (e.g. error pages). */
  path?: string;
  /** Set true to add a noindex,follow robots meta tag (e.g. error pages). */
  noindex?: boolean;
  /** JSON-LD object(s) for this route. Server-injected on initial load (see server/_core/vite.ts); re-applied here so client-side navigation between routes doesn't leave a stale route's schema in the DOM. */
  jsonLd?: object | object[];
}

function setMetaTag(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMetaTag(attr: "name" | "property", key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]'
  );
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function removeCanonical() {
  document.head.querySelector('link[rel="canonical"]')?.remove();
}

const JSON_LD_ATTR = "data-seo-jsonld";

function setJsonLd(jsonLd: object | object[]) {
  let el = document.head.querySelector<HTMLScriptElement>(`script[${JSON_LD_ATTR}]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute(JSON_LD_ATTR, "true");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(jsonLd);
}

function removeJsonLd() {
  document.head.querySelector(`script[${JSON_LD_ATTR}]`)?.remove();
}

/**
 * Sets document.title, meta description, Open Graph/Twitter tags, canonical
 * link, and JSON-LD for the current route. server/_core/vite.ts injects the
 * same values server-side on initial load (so crawlers that don't execute
 * JavaScript see the right metadata too); this re-applies them once React
 * mounts and again on every client-side route change, since a soft
 * navigation doesn't re-request the server-rendered HTML.
 */
export function useSeo({ title, description, path, noindex, jsonLd }: SeoOptions) {
  useEffect(() => {
    document.title = title;
    setMetaTag("name", "description", description);
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);

    if (path) {
      const canonicalUrl = `${SITE_URL}${path}`;
      setCanonical(canonicalUrl);
      setMetaTag("property", "og:url", canonicalUrl);
    } else {
      // Pages that shouldn't declare a canonical (e.g. NotFound) must
      // actively clear any tag left over from a prior client-side route —
      // the previous page's canonical/og:url would otherwise persist.
      removeCanonical();
      removeMetaTag("property", "og:url");
    }

    if (noindex) {
      setMetaTag("name", "robots", "noindex, follow");
    } else {
      removeMetaTag("name", "robots");
    }

    if (jsonLd) {
      setJsonLd(jsonLd);
    } else {
      removeJsonLd();
    }
  }, [title, description, path, noindex, jsonLd]);
}
